import React from "react";
import styled from "styled-components";
import { FaQuoteLeft } from "react-icons/fa";
import AnimateOnScroll from "../AnimateOnScroll";

export default function Reviews() {

  return (
    <Section id="reviews">
      <div className="container">
        <AnimateOnScroll animation="fadeUp" delay={0} duration={800}>
          <HeaderInfo>
            <h1 className="font40 extraBold">What Our Students Say</h1>
            <p className="font16">
              Hear from our successful graduates who have achieved their academic and professional goals through AFRIQ's comprehensive programs.
            </p>
          </HeaderInfo>
        </AnimateOnScroll>

        <ReviewsContainer>
          <AnimateOnScroll animation="scaleUp" delay={200} duration={800}>
            <NoReviewsMessage>
              <FaQuoteLeft />
              <p className="font18 semiBold">No reviews available</p>
              <p className="font14">Check back later for student testimonials.</p>
            </NoReviewsMessage>
          </AnimateOnScroll>
        </ReviewsContainer>

        <StatsSection>
          <AnimateOnScroll animation="fadeUp" delay={400} duration={800}>
            <StatsGrid>
              <AnimateOnScroll animation="scaleUp" delay={600} duration={800}>
                <StatCard>
                  <StatNumber className="font40 extraBold gradient-text">500+</StatNumber>
                  <StatLabel className="font16 semiBold">Students Prepared</StatLabel>
                  <StatDescription className="font14">Successfully guided through our programs</StatDescription>
                </StatCard>
              </AnimateOnScroll>
              
              <AnimateOnScroll animation="scaleUp" delay={800} duration={800}>
                <StatCard>
                  <StatNumber className="font40 extraBold gradient-text">95%</StatNumber>
                  <StatLabel className="font16 semiBold">Success Rate</StatLabel>
                  <StatDescription className="font14">Admission rate to professional schools</StatDescription>
                </StatCard>
              </AnimateOnScroll>
              
              <AnimateOnScroll animation="scaleUp" delay={1000} duration={800}>
                <StatCard>
                  <StatNumber className="font40 extraBold gradient-text">50+</StatNumber>
                  <StatLabel className="font16 semiBold">Scholarships Awarded</StatLabel>
                  <StatDescription className="font14">Financial support provided to students</StatDescription>
                </StatCard>
              </AnimateOnScroll>
              
              <AnimateOnScroll animation="scaleUp" delay={1200} duration={800}>
                <StatCard>
                  <StatNumber className="font40 extraBold gradient-text">15+</StatNumber>
                  <StatLabel className="font16 semiBold">Years Experience</StatLabel>
                  <StatDescription className="font14">Dedicated to educational excellence</StatDescription>
                </StatCard>
              </AnimateOnScroll>
            </StatsGrid>
          </AnimateOnScroll>
        </StatsSection>
      </div>
    </Section>
  );
}

const Section = styled.section`
  padding: 120px 0;
  background: #4b2e83;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
  
  @media (max-width: 480px) {
    padding: 60px 0;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 20% 80%, rgba(75, 46, 131, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(75, 46, 131, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(75, 46, 131, 0.05) 0%, transparent 50%);
    z-index: 1;
  }
`;

const HeaderInfo = styled.div`
  margin-bottom: 60px;
  text-align: center;
  position: relative;
  z-index: 2;
  
  h1 {
    margin-bottom: 15px;
    color: white;
    font-size: clamp(1.75rem, 4vw, 2.5rem);
    line-height: 1.3;
  }
  
  p {
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.7;
    max-width: 700px;
    margin: 0 auto;
    font-size: clamp(0.9rem, 2vw, 0.938rem);
  }
`;

const ReviewsContainer = styled.div`
  position: relative;
  z-index: 2;
  margin-bottom: 80px;
`;

const NoReviewsMessage = styled.div`
  background: rgba(75, 46, 131, 0.95);
  backdrop-filter: blur(20px);
  padding: 60px 40px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
  color: white;
  
  svg {
    font-size: 48px;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 20px;
  }
  
  p {
    margin: 10px 0;
    color: rgba(255, 255, 255, 0.9);
  }
  
  @media (max-width: 768px) {
    padding: 50px 30px;
  }
  
  @media (max-width: 480px) {
    padding: 40px 20px;
  }
`;

const StatsSection = styled.div`
  position: relative;
  z-index: 2;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: rgba(75, 46, 131, 0.95);
  backdrop-filter: blur(20px);
  padding: 30px 20px;
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
  transition: all 0.3s ease;
  color: white;
  
  @media (max-width: 768px) {
    padding: 25px 18px;
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 22px 15px;
    border-radius: 10px;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
  }
`;

const StatNumber = styled.div`
  margin-bottom: 10px;
  color: white;
`;

const StatLabel = styled.div`
  color: white;
  margin-bottom: 8px;
`;

const StatDescription = styled.div`
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.4;
`;
